import { useEffect, useMemo } from 'react';
import { useItemsBulkFetchV2Query } from '@/common/queries/useGetItemsBulkFetchV2Query';
import { useObjectivesByLoopIdInfiniteQuery } from '@/features/objectives/queries';
import { useGetTeamsByIdQuery } from '@/features/teams/queries/useGetTeamsQuery';
import type { Finding } from './types';
import { collectFindingReferences } from './types';

// Only the two fields a reference chip shows — narrower than DEFAULT_FIELD_IDS, since this lookup
// exists purely to label a chip, not to hydrate a full item.
const ITEM_REF_FIELD_IDS = ['name', 'externalKey'];

export interface ResolvedItemRef {
  externalKey?: string;
  name?: string;
}

export interface FindingRefsLookup {
  /** `undefined` while the batch fetch is loading or the id resolved to nothing (e.g. deleted). */
  item: (id: number) => ResolvedItemRef | undefined;
  /** `undefined` while objectives are still loading or the id resolved to nothing. */
  objective: (id: number) => string | undefined;
  /** `undefined` while the loop's teams are loading or the id is not a team in this loop. */
  team: (id: number) => string | undefined;
}

const scopeRefId = (finding: Finding, level: 'initiative' | 'team'): number | undefined => {
  if (finding.scope?.level !== level || !finding.scope.ref) return undefined;
  const id = Number(finding.scope.ref);
  return Number.isInteger(id) ? id : undefined;
};

// A Loop initiative is an item, so its scope ref resolves through the same bulk fetch as the
// `[item:id]` tokens; a team needs the loop's team list.
const collectReferencedIds = (
  findings: Finding[],
): { items: number[]; objectives: number[]; teams: number[] } => {
  const items = new Set<number>();
  const objectives = new Set<number>();
  const teams = new Set<number>();
  for (const finding of findings) {
    const refs = collectFindingReferences(finding);
    for (const id of refs.items) items.add(id);
    for (const id of refs.objectives) objectives.add(id);
    const initiativeId = scopeRefId(finding, 'initiative');
    if (initiativeId !== undefined) items.add(initiativeId);
    const teamId = scopeRefId(finding, 'team');
    if (teamId !== undefined) teams.add(teamId);
  }
  return { items: [...items], objectives: [...objectives], teams: [...teams] };
};

/**
 * Resolves every `[item:id]` / `[objective:id]` reference token across a run's findings to a
 * display label, using the same batch-lookup queries the rest of the app already has —
 * `useItemsBulkFetchV2Query` (precedented by `useAffectedHealthItems`) for items, the objectives
 * list for objectives, since there is no fetch-objectives-by-id endpoint anywhere in the stack.
 * Resolving on the frontend rather than storing a snapshot on `Run` means a label reflects the
 * viewer's own read access and stays current instead of freezing at generation time.
 */
export const useFindingRefs = (findings: Finding[], loopId: number): FindingRefsLookup => {
  const {
    items: itemIds,
    objectives: objectiveIds,
    teams: teamIds,
  } = useMemo(() => collectReferencedIds(findings), [findings]);

  const { data: itemsResult } = useItemsBulkFetchV2Query(
    { itemIds, fieldIds: ITEM_REF_FIELD_IDS },
    undefined,
    itemIds.length > 0,
  );

  const { data: teamsById } = useGetTeamsByIdQuery({
    loopId: String(loopId),
    enabled: teamIds.length > 0,
  });

  const objectivesQuery = useObjectivesByLoopIdInfiniteQuery({ loopId });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = objectivesQuery;

  const objectiveTitleById = useMemo(() => {
    const map = new Map<number, string>();
    for (const objective of objectivesQuery.data ?? []) {
      if (objective) map.set(objective.id, objective.title);
    }
    return map;
  }, [objectivesQuery.data]);

  // Every objective this run's findings actually reference has resolved, so there is nothing left
  // to look for — stop before exhausting the rest of a large org's objective list.
  const allObjectivesFound = objectiveIds.every((id) => objectiveTitleById.has(id));

  // This hook needs the complete id -> title map, not one page of it, so it pages through rather
  // than the load-more-on-scroll pattern the objectives list view uses — but only as far as it
  // takes to find every id actually referenced, not the whole list.
  useEffect(() => {
    if (objectiveIds.length > 0 && !allObjectivesFound && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [objectiveIds.length, allObjectivesFound, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo<FindingRefsLookup>(
    () => ({
      item: (id) => {
        const item = itemsResult?.itemsById?.[String(id)];
        if (!item) return undefined;
        return { externalKey: item.externalKey ?? undefined, name: item.name };
      },
      objective: (id) => objectiveTitleById.get(id),
      team: (id) => teamsById?.[id]?.name,
    }),
    [itemsResult, objectiveTitleById, teamsById],
  );
};
